import React from "react"
import { useNavigate, useParams } from "react-router-dom"
import _ from "lodash"
import { Button, Chip } from "@mui/material"
import {
    ArrowBack,
    LockOutlined,
    PlayCircleOutline,
    PersonOutline,
    CheckCircle,
    Star,
    AttachFileOutlined,
    VisibilityOutlined
} from "@mui/icons-material"
import { Loader, ToastMessage } from "../../components/index"
import { useGetCourseDetailQuery } from "../../Redux/actions/lecture.action"
import { learnPaths } from "../constants"
import { formatPrice } from "../components/price"
import { formatAccessUntil } from "../components/access"
import LectureCard from "../components/LectureCard"
import CheckoutDialog from "../components/CheckoutDialog"
import EmptyState from "../components/EmptyState"
import ChapterSection from "../components/ChapterSection"
import CourseReviews from "../components/CourseReviews"
import { plural } from "../components/datetime"
import usePurchaseFlow from "../usePurchaseFlow"
import { groupLecturesByChapter, isLectureLocked } from "../chapters"

/**
 * Course page. Non-enrolled students get the full syllabus with every lecture
 * locked except the free previews, plus a buy button; enrolled students can
 * play any lecture. The syllabus is grouped into collapsible chapters when
 * the course has any, and the course's reviews sit underneath.
 */
export default function CourseDetailPage() {
    const { course_id } = useParams()
    const navigate = useNavigate()

    const { data, isLoading, isError, error } = useGetCourseDetailQuery({
        course_id
    })

    const purchase = usePurchaseFlow()

    const course = _.get(data, "course", null)
    const lectures = React.useMemo(() => _.get(data, "lectures", []), [data])
    const chapters = React.useMemo(
        () => _.get(data, "chapters", null) || [],
        [data]
    )
    const enrolled = Boolean(_.get(data, "is_enrolled", false))
    const accent = course?.accent || "#4f46e5"

    const sections = React.useMemo(
        () => groupLecturesByChapter(chapters, lectures),
        [chapters, lectures]
    )

    /* Lectures are numbered straight through the course, across chapters. */
    const numbering = React.useMemo(
        () => new Map(lectures.map((item, index) => [item.lecture_id, index])),
        [lectures]
    )

    const firstPreview = enrolled
        ? null
        : lectures.find((item) => !isLectureLocked(item, false))
    const resourceTotal = _.sumBy(lectures, (item) => item.resource_count || 0)

    /* Locked lectures lead to the buy flow; everything else plays. */
    const openLecture = (lecture) => {
        if (isLectureLocked(lecture, enrolled)) {
            purchase.startPurchase(course)
            return
        }
        navigate(learnPaths.lecture(course_id, lecture.lecture_id))
    }

    const renderLectures = (items) => (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {items.map((lecture) => (
                <LectureCard
                    key={lecture.lecture_id}
                    lecture={lecture}
                    index={numbering.get(lecture.lecture_id)}
                    locked={isLectureLocked(lecture, enrolled)}
                    preview={!enrolled && Boolean(lecture.is_preview)}
                    onSelect={openLecture}
                />
            ))}
        </div>
    )

    if (isError) {
        return (
            <EmptyState
                icon={<PlayCircleOutline />}
                title="Course unavailable"
                description={
                    _.get(error, "data.detail", null) ||
                    "This course could not be loaded."
                }
                actionLabel="Back to catalogue"
                onAction={() => navigate(learnPaths.home)}
            />
        )
    }

    return (
        <div className="space-y-6">

            <Button
                size="small"
                startIcon={<ArrowBack />}
                onClick={() => navigate(learnPaths.home)}
            >
                All courses
            </Button>

            {/* Course header */}
            <section
                className="rounded-2xl p-5 text-white shadow-lg sm:p-8"
                style={{
                    background: `linear-gradient(135deg, ${accent} 0%, ${accent}bb 55%, #0f172a 100%)`
                }}
            >
                <div className="flex flex-wrap items-center gap-2">
                    {course?.class_name && (
                        <Chip
                            size="small"
                            label={course.class_name}
                            sx={{
                                bgcolor: "rgba(255,255,255,0.2)",
                                color: "#fff"
                            }}
                        />
                    )}
                    {course?.subject_name && (
                        <Chip
                            size="small"
                            label={course.subject_name}
                            sx={{
                                bgcolor: "rgba(255,255,255,0.2)",
                                color: "#fff"
                            }}
                        />
                    )}
                    {enrolled && (
                        <span className="flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                            <CheckCircle sx={{ fontSize: 14 }} />
                            {formatAccessUntil(_.get(data, "expires_at"))
                                ? `Access until ${formatAccessUntil(_.get(data, "expires_at"))}`
                                : "Enrolled"}
                        </span>
                    )}
                </div>

                <h1 className="mt-3 text-xl font-bold sm:text-3xl">
                    {course?.title || "Loading..."}
                </h1>

                {course?.description && (
                    <p className="mt-2 max-w-3xl text-sm text-white/85">
                        {course.description}
                    </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
                    {course?.instructor && (
                        <span className="flex items-center gap-1">
                            <PersonOutline sx={{ fontSize: 16 }} />
                            {course.instructor}
                        </span>
                    )}
                    {course?.rating_count > 0 && (
                        <span className="flex items-center gap-1">
                            <Star sx={{ fontSize: 16, color: "#fbbf24" }} />
                            <span className="font-semibold text-white">
                                {Number(course.rating_average || 0).toFixed(1)}
                            </span>
                            ({plural(course.rating_count, "rating")})
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        <PlayCircleOutline sx={{ fontSize: 16 }} />
                        {chapters.length > 0
                            ? `${plural(chapters.length, "chapter")} · `
                            : ""}
                        {plural(lectures.length, "lecture")}
                    </span>
                    {resourceTotal > 0 && (
                        <span className="flex items-center gap-1">
                            <AttachFileOutlined sx={{ fontSize: 16 }} />
                            {plural(resourceTotal, "resource")}
                        </span>
                    )}
                </div>

                {/* Buy / continue */}
                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                    {!enrolled && course && (
                        <>
                            <span className="text-2xl font-bold">
                                {formatPrice(course.price, course.currency)}
                            </span>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: "#fff",
                                    color: "#0f172a",
                                    fontWeight: 700,
                                    "&:hover": { bgcolor: "#f1f5f9" }
                                }}
                                onClick={() => purchase.startPurchase(course)}
                            >
                                {Number(course.price) > 0
                                    ? "Buy this course"
                                    : "Enrol for free"}
                            </Button>
                            {firstPreview && (
                                <Button
                                    variant="outlined"
                                    startIcon={<VisibilityOutlined />}
                                    sx={{
                                        color: "#fff",
                                        borderColor: "rgba(255,255,255,0.7)",
                                        "&:hover": {
                                            borderColor: "#fff",
                                            bgcolor: "rgba(255,255,255,0.1)"
                                        }
                                    }}
                                    onClick={() => openLecture(firstPreview)}
                                >
                                    Watch a free preview
                                </Button>
                            )}
                        </>
                    )}

                    {enrolled && lectures.length > 0 && (
                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: "#fff",
                                color: "#0f172a",
                                fontWeight: 700,
                                "&:hover": { bgcolor: "#f1f5f9" }
                            }}
                            onClick={() =>
                                navigate(
                                    learnPaths.lecture(
                                        course_id,
                                        lectures[0].lecture_id
                                    )
                                )
                            }
                        >
                            Start watching
                        </Button>
                    )}
                </div>
            </section>

            {/* Syllabus */}
            <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <h2 className="text-base font-semibold text-slate-800 sm:text-lg">
                        Course content
                    </h2>
                    {!enrolled && lectures.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                            <LockOutlined sx={{ fontSize: 14 }} />
                            {firstPreview
                                ? "Previews are free · enrol for the rest"
                                : "Enrol to unlock"}
                        </span>
                    )}
                </div>

                {!isLoading && lectures.length === 0 && (
                    <EmptyState
                        icon={<PlayCircleOutline />}
                        title="No lectures published yet"
                        description="Lectures will appear here as soon as the teacher uploads them."
                    />
                )}

                {/* Without chapters the syllabus stays one plain list. */}
                {chapters.length === 0 && renderLectures(lectures)}

                {chapters.length > 0 && (
                    <div className="space-y-3">
                        {sections.map(({ chapter, lectures: items }, index) => (
                            <ChapterSection
                                key={chapter ? chapter.chapter_id : "ungrouped"}
                                title={chapter ? chapter.title : "More lectures"}
                                description={chapter?.description}
                                lectureCount={items.length}
                                progress={enrolled ? chapter?.progress : null}
                                defaultExpanded={index === 0}
                            >
                                {items.length > 0 ? (
                                    renderLectures(items)
                                ) : (
                                    <p className="text-sm text-slate-500">
                                        Lectures for this chapter are coming
                                        soon.
                                    </p>
                                )}
                            </ChapterSection>
                        ))}
                    </div>
                )}
            </section>

            {/* Reviews */}
            {course && (
                <CourseReviews course_id={course_id} enrolled={enrolled} />
            )}

            <CheckoutDialog
                open={Boolean(purchase.selectedCourse)}
                course={purchase.selectedCourse}
                saving={purchase.isPurchasing}
                onClose={purchase.cancelPurchase}
                onConfirm={purchase.confirmPurchase}
            />

            {(isLoading || purchase.isPurchasing) && (
                <Loader show={true} message="Loading course..." />
            )}

            {purchase.notification.show && (
                <ToastMessage
                    {...purchase.notification}
                    onClose={purchase.clearNotification}
                />
            )}
        </div>
    )
}
